import {
  Building2,
  Monitor,
  RefreshCcw,
  Search,
  Trash2,
  Users2,
} from "lucide-react";
import {
  IconGavel,
  IconUserEdit,
  IconHourglassEmpty,
  IconPencil,
  IconBuildingBank,
  IconDeviceDesktop,
} from "@tabler/icons-react";
import {
  DC_ORANGE,
  HC_BLUE,
  // benchRoomLabel,
  // complexLabel,
  // courtRoomLabel,
  // districtLabel,
  // highCourtLabel,
  initials,
  isAssignableRole,
  officerName,
  refId,
  roleLabel,
} from "./helpers";
import type {
  BenchRoomRef,
  CourtOfficer,
  CourtOfficerCourtType,
  CourtOfficerRole,
  CourtRoomRef,
  HighCourtRef,
  // DistrictRef,
  // ComplexRef,
} from "./types";
import Pagination from "../../../utils/Pagination";

interface CourtOfficerTableProps {
  officers: CourtOfficer[];
  courtType: CourtOfficerCourtType;
  activeRole: CourtOfficerRole | "all";
  search: string;
  status: string;
  assignment: string;
  page: number;
  totalPages: number;
  isLoading: boolean;
  benchRooms: BenchRoomRef[];
  courtRooms: CourtRoomRef[];
  highCourts: HighCourtRef[];
  roleCounts: Record<CourtOfficerRole | "all", number>;
  onRoleChange: (role: CourtOfficerRole | "all") => void;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onAssignmentChange: (value: string) => void;
  onPageChange: (page: number) => void;
  onAdd: () => void;
  onEdit: (officer: CourtOfficer) => void;
  onAssign: (officer: CourtOfficer) => void;
  onDelete: (officer: CourtOfficer) => void;
}

const roleCards: Array<{
  value: CourtOfficerRole | "all";
  label: string;
  icon: typeof Users2;
  iconColor?: string;
}> = [
  { value: "all", label: "All Officers", icon: Users2 },
  {
    value: "Judge",
    label: "Judges",
    icon: IconGavel,
    iconColor: "#F26422",
  },
  {
    value: "Reader",
    label: "Readers",
    icon: IconUserEdit,
    iconColor: "#16A34A",
  },
  {
    value: "Controller",
    label: "Controllers",
    icon: Monitor,
    iconColor: "#7C3AED",
  },
];
const statusClass = (status?: string) =>
  status === "Inactive"
    ? "bg-slate-100 text-slate-600"
    : "bg-emerald-100 text-emerald-700";

function assignmentLabel(officer: CourtOfficer) {
  if (officer.courtType === "district_courts") {
    const districtName =
      typeof officer.district === "object" && officer.district
        ? officer.district.districtName
        : "";

    const complexName =
      typeof officer.complex === "object" && officer.complex
        ? officer.complex.complexName
        : "";

    if (districtName && complexName) {
      return `${districtName}, ${complexName}`;
    }

    if (districtName) {
      return districtName;
    }

    if (complexName) {
      return complexName;
    }

    return "Pending assignment";
  }

  if (officer.role === "Judge") {
    const highCourtName =
      typeof officer.highcourt === "object" && officer.highcourt
        ? officer.highcourt.highCourtName
        : "";

    const benchName =
      typeof officer.bench === "object" && officer.bench
        ? officer.bench.location
        : "";

    if (highCourtName && benchName) {
      return `${highCourtName} - ${benchName}`;
    }

    if (highCourtName) {
      return highCourtName;
    }

    return "Pending assignment";
  }
  if (officer.courtType === "high_courts" && officer.role !== "Judge") {
    const highCourtName =
      typeof officer.highcourt === "object" && officer.highcourt
        ? officer.highcourt.highCourtName
        : "";

    // New structure
    if (Array.isArray(officer.benchRooms) && officer.benchRooms.length > 0) {
      return highCourtName || "Assigned High Court";
    }

    // Previous functionality
    if (typeof officer.benchRoom === "object" && officer.benchRoom) {
      return highCourtName || "Assigned High Court";
    }

    if (typeof officer.bench === "object" && officer.bench) {
      return highCourtName || officer.bench.benchName;
    }

    return refId(officer.benchRoom) || refId(officer.bench)
      ? highCourtName || "Assigned High Court"
      : "Pending assignment";
  }
  return officer.courtRoomNo || "Pending";
}

function controllerCourtroomCount(officer: CourtOfficer) {
  if (officer.role !== "Controller") return 0;

  // High Court Controller → benchRooms
  if (officer.courtType === "high_courts") {
    return Array.isArray(officer.benchRooms) ? officer.benchRooms.length : 0;
  }

  // District Court Controller → courtrooms
  if (officer.courtType === "district_courts") {
    return Array.isArray(officer.courtrooms) ? officer.courtrooms.length : 0;
  }

  return 0;
}
function courtroomLabel(officer: CourtOfficer) {
  // District Court
  if (officer.courtType === "district_courts") {
    // District Court Judge → show courtRoom name
    if (officer.role === "Judge") {
      if (typeof officer.courtRoom === "object" && officer.courtRoom) {
        return (
          officer.courtRoom.courtRoomName ||
          officer.courtRoom.roomNumber ||
          "Assigned courtroom"
        );
      }

      return refId(officer.courtRoom) ? "Assigned courtroom" : "Pending";
    }
    const assignedCourtrooms = officer.courtrooms;

    if (!Array.isArray(assignedCourtrooms) || assignedCourtrooms.length === 0) {
      return "Pending";
    }

    // Controller → show count
    if (officer.role === "Controller") {
      return `${assignedCourtrooms.length} ${
        assignedCourtrooms.length === 1 ? "room" : "rooms"
      }`;
    }

    // Judge / Reader → show latest assigned courtroom name
    const latestRoom = assignedCourtrooms[assignedCourtrooms.length - 1];

    if (typeof latestRoom === "object" && latestRoom) {
      return (
        latestRoom.courtRoomName ||
        latestRoom.roomNumber ||
        "Assigned courtroom"
      );
    }

    return "Assigned courtroom";
  }

  // High Court Reader / Controller
  if (officer.courtType === "high_courts" && officer.role !== "Judge") {
    // New structure
    if (Array.isArray(officer.benchRooms) && officer.benchRooms.length > 0) {
      if (officer.role === "Controller") {
        return `${officer.benchRooms.length} ${
          officer.benchRooms.length === 1 ? "room" : "rooms"
        }`;
      }

      const room = officer.benchRooms[0];

      if (typeof room === "object" && room) {
        return room.benchName || room.courtRoom || "Assigned Court Hall";
      }

      return "Assigned Court Hall";
    }

    // Previous functionality
    if (typeof officer.benchRoom === "object" && officer.benchRoom) {
      return (
        officer.benchRoom.benchName ||
        officer.benchRoom.courtRoom ||
        "Assigned Court Hall"
      );
    }

    if (typeof officer.bench === "object" && officer.bench) {
      return officer.bench.benchName;
    }

    return refId(officer.benchRoom) || refId(officer.bench)
      ? "Assigned Bench"
      : "Pending";
  }

  // High Court Judge
  // High Court Judge → show benchName
  if (officer.courtType === "high_courts" && officer.role === "Judge") {
    // First check benchRoom
    if (typeof officer.benchRoom === "object" && officer.benchRoom) {
      return officer.benchRoom.benchName || "Assigned Bench";
    }

    // Fallback to bench
    if (typeof officer.bench === "object" && officer.bench) {
      return officer.bench.benchName || "Assigned Bench";
    }

    return refId(officer.benchRoom) || refId(officer.bench)
      ? "Assigned Bench"
      : "Pending";
  }

  return officer.courtRoomNo || "Pending";
}

function rolePillColor(role?: string, courtType?: CourtOfficerCourtType) {
  if (role === "Judge") {
    return courtType === "district_courts"
      ? "bg-orange-50 text-orange-700"
      : "bg-blue-50 text-blue-700";
  }
  if (role === "Controller") return "bg-purple-50 text-purple-700";
  return "bg-emerald-50 text-emerald-700";
}

export default function CourtOfficerTable({
  officers,
  courtType,
  activeRole,
  search,
  status,
  assignment,
  page,
  totalPages,
  isLoading,
  // benchRooms,
  // courtRooms,
  roleCounts,
  onRoleChange,
  onSearchChange,
  onStatusChange,
  onAssignmentChange,
  onPageChange,
  onAdd,
  onEdit,
  onAssign,
  onDelete,
}: CourtOfficerTableProps) {
  const accent = courtType === "district_courts" ? DC_ORANGE : HC_BLUE;
  const accentText =
    courtType === "district_courts" ? "text-orange-600" : "text-blue-600";
  const displayedOfficers = officers;
  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {roleCards.map((card) => {
          const Icon = card.icon;
          const customIconColor = card.iconColor;
          const selected = activeRole === card.value;
          const count = roleCounts[card.value] ?? 0;

          return (
            <button
              type="button"
              key={card.value}
              onClick={() => onRoleChange(card.value)}
              className={`flex h-[90px] items-center gap-5 rounded-[8px] border bg-white p-5 text-left shadow-sm transition 
                ${
                  selected
                    ? "border-slate-400"
                    : "border-slate-200 hover:border-slate-300"
                }`}
            >
              <span
                className="flex h-14 w-14 items-center justify-center rounded-[12px]"
                style={{
                  background:
                    card.value === "Controller"
                      ? "#F3E8FF"
                      : card.value === "Reader"
                        ? "#DCFCE7"
                        : card.value === "Judge"
                          ? courtType === "district_courts"
                            ? "#FFF4EE"
                            : "#DBEAFE"
                          : "#F1F5F9",
                  color:
                    card.value === "Controller"
                      ? "#7C3AED"
                      : card.value === "Reader"
                        ? "#16A34A"
                        : card.value === "Judge"
                          ? courtType === "district_courts"
                            ? "#F26422"
                            : "#2563EB"
                          : "#64748B",
                }}
              >
                <Icon
                  size={24}
                  color={
                    card.value === "Judge"
                      ? courtType === "district_courts"
                        ? "#F26422"
                        : "#1D4ED8"
                      : customIconColor
                  }
                />
              </span>
              <span>
                <span
                  className="block text-xs font-semibold"
                  style={{
                    color:
                      card.value === "Controller"
                        ? "#7C3AED"
                        : card.value === "Reader"
                          ? "#047857"
                          : card.value === "Judge"
                            ? courtType === "district_courts"
                              ? "#F26422"
                              : "#2563EB"
                            : "#64748B",
                  }}
                >
                  {card.label}
                </span>
                <span
                  className="text-3xl font-bold"
                  style={{
                    color:
                      card.value === "Controller"
                        ? "#374151"
                        : card.value === "Reader"
                          ? "#047857"
                          : card.value === "Judge"
                            ? courtType === "district_courts"
                              ? "#F26422"
                              : "#1D4ED8"
                            : "#374151",
                  }}
                >
                  {count}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <label className="relative flex-1">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search by name or username..."
            className="h-11 w-full rounded-[8px] border border-slate-200 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
          />
        </label>
        <div className="flex flex-wrap items-center gap-2 border-l border-slate-200 pl-3">
          <span className="text-xs font-bold text-slate-400">Status</span>
          {[
            ["", "All"],
            ["Active", "Active"],
            ["Inactive", "Inactive"],
          ].map(([value, label]) => (
            <button
              type="button"
              key={value}
              onClick={() => onStatusChange(value)}
              className={`h-9 rounded-full px-4 text-sm font-semibold ${
                status === value
                  ? "bg-[#2563eb] text-white"
                  : "border border-slate-200 bg-white text-slate-600"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 border-l border-slate-200 pl-3">
          <span className="text-xs font-bold text-slate-400">Assignment</span>
          {[
            ["", "All"],
            ["assigned", "Assigned"],
            ["pending", "Pending"],
          ].map(([value, label]) => (
            <button
              type="button"
              key={value}
              onClick={() => onAssignmentChange(value)}
              className={`h-9 rounded-full px-4 text-sm font-semibold ${
                assignment === value
                  ? "bg-[#2563eb] text-white"
                  : "border border-slate-200 bg-white text-slate-600"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-[8px] border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-[1120px] w-full text-left">
            <thead className="bg-slate-50 text-xs uppercase tracking-[0.08em] text-slate-500">
              <tr>
                <th className="px-5 py-4 font-bold">Officer</th>
                <th className="px-5 py-4 font-bold">Role</th>
                <th className="px-5 py-4 font-bold">Assigned To</th>
                <th className="px-5 py-4 font-bold">Courtrooms</th>
                <th className="px-5 py-4 font-bold">Status</th>
                <th className="px-5 py-4 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-5 py-16 text-center text-sm text-slate-500"
                  >
                    Loading court officers...
                  </td>
                </tr>
              ) : displayedOfficers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-16 text-center">
                    <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-[8px] border border-slate-200 bg-slate-50 text-slate-300">
                      <Users2 size={22} />
                    </div>
                    <p className="text-sm font-semibold text-slate-500">
                      No officers found
                    </p>
                    <button
                      type="button"
                      onClick={onAdd}
                      className={`mt-4 inline-flex rounded-[8px] px-4 py-2 text-sm font-semibold ${accentText} bg-slate-50`}
                    >
                      + Create Officer
                    </button>
                  </td>
                </tr>
              ) : (
                displayedOfficers.map((officer) => {
                  const assignable = isAssignableRole(officer.role);
                  const assignedTo = assignmentLabel(officer);

                  const controllerRoomCount = controllerCourtroomCount(officer);

                  const courtrooms =
                    officer.role === "Controller"
                      ? controllerRoomCount === 0
                        ? "Pending"
                        : controllerRoomCount === 1
                          ? "1 room"
                          : `${controllerRoomCount} rooms`
                      : courtroomLabel(officer);

                  const pending =
                    officer.role === "Judge"
                      ? officer.courtType === "district_courts"
                        ? !refId(officer.courtRoom)
                        : !refId(officer.benchRoom) && !refId(officer.bench)
                      : officer.courtType === "high_courts" &&
                          (officer.role === "Reader" ||
                            officer.role === "Controller")
                        ? !(
                            (Array.isArray(officer.benchRooms) &&
                              officer.benchRooms.length > 0) ||
                            refId(officer.benchRoom) ||
                            refId(officer.bench)
                          )
                        : officer.role === "Controller"
                          ? controllerRoomCount === 0
                          : courtrooms === "Pending";

                  return (
                    <tr
                      key={officer._id || officer.id}
                      className="border-t border-slate-100"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <span
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold"
                            style={{
                              background:
                                officer.role === "Controller"
                                  ? "#FDF4FF"
                                  : officer.role === "Reader"
                                    ? "#F0FDF4"
                                    : officer.role === "Judge" &&
                                        officer.courtType === "district_courts"
                                      ? "#FFF4EE"
                                      : "#E8EEF7",

                              borderColor:
                                officer.role === "Controller"
                                  ? "#9333EA"
                                  : officer.role === "Reader"
                                    ? "#16A34A"
                                    : officer.role === "Judge" &&
                                        officer.courtType === "district_courts"
                                      ? "#F26422"
                                      : accent,

                              color:
                                officer.role === "Controller"
                                  ? "#7E22CE"
                                  : officer.role === "Reader"
                                    ? "#15803D"
                                    : officer.role === "Judge" &&
                                        officer.courtType === "district_courts"
                                      ? "#C24E14"
                                      : accent,
                            }}
                          >
                            {initials(officer)}
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate font-semibold text-gray-700">
                              {officerName(officer)}
                            </span>
                            <span className="block truncate text-xs text-slate-400">
                              @{officer.username || "username"}
                            </span>
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${rolePillColor(
                            officer.role,
                            courtType,
                          )}`}
                        >
                          {officer.role === "Judge"
                            ? "Judge"
                            : roleLabel(officer.role, courtType)}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600">
                        {assignedTo}
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex items-center gap-[5px] max-w-[160px] overflow-hidden whitespace-nowrap text-ellipsis rounded-full px-[9px] py-[3px] ${
                            pending
                              ? "inline-flex items-center gap-[5px] w-fit whitespace-nowrap rounded-full border border-[#FDE68A] bg-[#FFFBEB] px-[9px] py-[3px] text-[11px] font-semibold text-[#D97706]"
                              : officer.role === "Reader"
                                ? "border border-[#BBF7D0] bg-[#DCFCE7] text-[#15803D] text-[11.5px] font-semibold"
                                : officer.role === "Controller"
                                  ? "inline-flex items-center gap-[5px] text-[11.5px] font-semibold text-[#7E22CE] bg-[#F3E8FF] border border-[#E9D5FF] px-[9px] py-[3px] rounded-[20px] cursor-pointer transition-all duration-150 whitespace-nowrap"
                                  : officer.role === "Judge" &&
                                      courtType === "high_courts"
                                    ? "border border-[#E2E8F0] bg-[#eef2ff] text-[#1d4ed8] text-[11px] font-medium"
                                    : officer.role === "Judge" &&
                                        courtType === "district_courts"
                                      ? "inline-flex items-center gap-[5px] text-[11px] font-medium text-[#C24E14] bg-[#FFF4EE] border border-[#FED7AA] px-[9px] py-[3px] rounded-[20px] max-w-[160px] overflow-hidden text-ellipsis whitespace-nowrap"
                                      : "border border-slate-200 bg-slate-50 text-slate-600 text-[11px] font-medium"
                          }`}
                        >
                          {pending ? (
                            <IconHourglassEmpty size={13} />
                          ) : officer.role === "Controller" ? (
                            <IconDeviceDesktop size={13} color="#7E22CE" />
                          ) : officer.role === "Judge" &&
                            courtType === "high_courts" ? (
                            <IconBuildingBank size={12} color="#64748B" />
                          ) : officer.role === "Judge" &&
                            courtType === "district_courts" ? (
                            <IconBuildingBank size={13} color="#C24E14" />
                          ) : (
                            <IconUserEdit size={13} color="#15803D" />
                          )}
                          <span className="truncate">{courtrooms}</span>
                        </span>
                        {pending && officer.role === "Judge" && (
                          <span className="mt-1 flex items-center gap-1 text-xs text-slate-400">
                            <Building2 size={11} />
                            Assign via Court Settings
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${statusClass(
                            officer.status,
                          )}`}
                        >
                          • {officer.status || "Active"}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => onEdit(officer)}
                            className="flex h-9 w-9 items-center justify-center rounded-[8px] border border-slate-200 text-blue-600 transition hover:bg-blue-50"
                            title="Edit officer"
                          >
                            <IconPencil size={12} color="#2563EB" />
                          </button>
                          {assignable && (
                            <button
                              type="button"
                              onClick={() => onAssign(officer)}
                              className="flex h-9 w-9 items-center justify-center rounded-[8px] border border-slate-200 text-emerald-600 transition hover:bg-emerald-50"
                              title="Assign courtroom"
                            >
                              <RefreshCcw size={16} />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => onDelete(officer)}
                            className="flex h-9 w-9 items-center justify-center rounded-[8px] border border-slate-200 text-red-500 transition hover:bg-red-50"
                            title="Delete officer"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={onPageChange}
      />
    </div>
  );
}
